/**
 * Link Components
 * React components for rendering and managing links in the editor
 */
import React, { useCallback, useEffect, useId, useRef } from 'react';
import { ReactEditor, useSlate } from 'slate-react';
import EditorButton from '../EditorButton/EditorButton';
import { isLinkActive, unwrapLink } from './link.utils.ts';
import './LinkModal.scss';

// ============================================================================
// Link Element (inline link in editor content)
// ============================================================================

export const LinkElement = ({ attributes, children, element, onHover, onLeave, editor }) => {
  const linkRef = useRef(null);

  const handleMouseEnter = () => {
    if (onHover && linkRef.current) {
      const rect = linkRef.current.getBoundingClientRect();
      // Get the path to this element so we can select it later
      try {
        const path = ReactEditor.findPath(editor, element);
        onHover({ element, path }, { x: rect.left, y: rect.bottom });
      } catch {
        // Element may no longer be in the document
        onHover({ element, path: null }, { x: rect.left, y: rect.bottom });
      }
    }
  };

  const handleMouseLeave = () => {
    if (onLeave) {
      onLeave();
    }
  };

  const handleClick = (e) => {
    // Only open link if Cmd/Ctrl is held (like Google Docs)
    if (e.metaKey || e.ctrlKey) {
      e.preventDefault();
      window.open(element.url, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <a
      className='inline-link-element'
      {...attributes}
      ref={linkRef}
      href={element.url}
      onClick={handleClick}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {children}
    </a>
  );
};

LinkElement.displayName = 'LinkElement';

// ============================================================================
// Link Leaf (for decorated URL text)
// ============================================================================

export const LinkLeaf = ({ attributes, children, leaf }) => {
  const handleClick = () => {
    const url = leaf.text;
    const hasProtocol = /^https?:\/\//i.test(url);
    window.open(hasProtocol ? url : `http://${url}`, '_blank', 'noopener,noreferrer');
  };

  return (
    <a
      className='styled-link'
      {...attributes}
      href={leaf.text}
      onClick={handleClick}
    >
      {children}
    </a>
  );
};

LinkLeaf.displayName = 'LinkLeaf';

// ============================================================================
// Link Modal
// ============================================================================

export const LinkModal = ({ isOpen, onClose, onSubmit, initialUrl, initialText, isEditing }) => {
  const [url, setUrl] = React.useState(initialUrl || '');
  const [text, setText] = React.useState(initialText || '');
  const urlInputRef = useRef(null);
  const textInputId = useId();
  const urlInputId = useId();

  useEffect(() => {
    if (isOpen) {
      setUrl(initialUrl || '');
      setText(initialText || '');
      // Focus the URL input when modal opens
      setTimeout(() => urlInputRef.current?.focus(), 100);
    }
  }, [isOpen, initialUrl, initialText]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (url.trim()) {
      onSubmit(url.trim(), text.trim());
    }
  };

  const handleKeyDown = (e) => {
    e.stopPropagation(); // Prevent dnd-kit listeners on parent from intercepting
    if (e.key === 'Escape') {
      onClose();
    }
  };

  if (!isOpen) {
    return null;
  }

  return (
    <div
      className='link-modal-overlay'
      onClick={onClose}
    >
      <div
        className='link-modal-container'
        onClick={(e) => e.stopPropagation()}
        onKeyDown={handleKeyDown}
      >
        <h3 className='link-modal-title'>{isEditing ? 'Edit link' : 'Insert link'}</h3>
        <form onSubmit={handleSubmit}>
          <div className='link-modal-input-group'>
            <label
              className='link-modal-label'
              htmlFor={textInputId}
            >
              Text to display
            </label>
            <input
              id={textInputId}
              className='link-modal-input'
              type='text'
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder='Enter display text'
            />
          </div>
          <div className='link-modal-input-group'>
            <label
              className='link-modal-label'
              htmlFor={urlInputId}
            >
              Link URL
            </label>
            <input
              id={urlInputId}
              className='link-modal-input'
              ref={urlInputRef}
              type='text'
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder='https://example.com'
            />
          </div>
          <div className='link-modal-buttons'>
            <button
              className='link-modal-button'
              type='button'
              onClick={onClose}
            >
              Cancel
            </button>
            <button
              className='link-modal-button link-modal-button--primary'
              type='submit'
              disabled={!url.trim()}
            >
              {isEditing ? 'Apply' : 'Insert'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

LinkModal.displayName = 'LinkModal';

// ============================================================================
// Link Tooltip (hover preview)
// ============================================================================

export const LinkTooltip = ({
  link,
  position,
  onEdit,
  onRemove,
  onClose,
  onMouseEnter,
  onMouseLeave,
}) => {
  const tooltipRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (tooltipRef.current && !tooltipRef.current.contains(e.target)) {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose]);

  if (!link || !position) {
    return null;
  }

  const url = link.element?.url || link.url;

  return (
    <div
      className='link-tooltip-container'
      ref={tooltipRef}
      style={{ left: position.x, top: position.y }}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <a
        className='link-tooltip-url'
        href={url}
        target='_blank'
        rel='noopener noreferrer'
      >
        {url}
      </a>
      <span className='link-tooltip-divider' />
      <button
        type='button'
        className='link-tooltip-button'
        onClick={onEdit}
        title='Edit link'
      >
        <span className='material-icons'>edit</span>
      </button>
      <button
        type='button'
        className='link-tooltip-button'
        onClick={onRemove}
        title='Remove link'
      >
        <span className='material-icons'>link_off</span>
      </button>
    </div>
  );
};

LinkTooltip.displayName = 'LinkTooltip';

// ============================================================================
// Link Button (toolbar button)
// ============================================================================

export const LinkButton = ({ onOpenModal }) => {
  const editor = useSlate();
  const isActive = isLinkActive(editor);

  const onMouseDown = useCallback(
    (event) => {
      event.preventDefault();
      if (isActive) {
        unwrapLink(editor);
      } else {
        onOpenModal();
      }
    },
    [editor, isActive, onOpenModal]
  );

  return (
    <EditorButton
      active={isActive}
      onMouseDown={onMouseDown}
      title={isActive ? 'Remove link' : 'Insert link (⌘K)'}
    >
      <span className='editor-icon material-icons'>{isActive ? 'link_off' : 'link'}</span>
    </EditorButton>
  );
};

LinkButton.displayName = 'LinkButton';
